import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Badge } from '@syneto/compass-react';
import { ValidityBadge } from './ValidityBadge';
import { ADDONS_PANEL_STATUS } from '../buildAddonsPanel';
import { ADDON_ROW_STATUS } from '../../../helpers/AddonValidity';
import { formatCalendarDate } from '../../../helpers/ExpiryRule';

const HEADING_ID = 'addons-heading';

// A grant that has not started is neither valid nor lapsed, so it gets its own calm badge instead of a validity one.
const PENDING_STATUS_LABEL = Object.freeze({
	[ADDON_ROW_STATUS.SCHEDULED]: 'Scheduled',
	[ADDON_ROW_STATUS.STARTS_ON_ACTIVATION]: 'Starts on activation',
});

const prefersReducedMotion = () => {
	return Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches);
};

const renderStatusBadge = (status) => {
	const pendingLabel = PENDING_STATUS_LABEL[status];

	return pendingLabel ? <Badge appearance="info">{pendingLabel}</Badge> : <ValidityBadge status={status} />;
};

// A scheduled grant shows when it starts; the others when they end: a forever grant reads "Forever", never a date,
// and a grant that starts on activation has no date yet, so nothing.
const renderDate = (row) => {
	if (row.status === ADDON_ROW_STATUS.SCHEDULED) {
		return renderTime(row.calendarDate, 'Starts ');
	}

	if (row.isForever) {
		return <span className="ms-2">Forever</span>;
	}

	return renderTime(row.calendarDate, 'Expires ');
};

const renderTime = (calendarDate, hiddenPrefix) => {
	if (!calendarDate) {
		return null;
	}

	return (
		<time className="ms-2" dateTime={calendarDate}>
			<span className="visually-hidden">{hiddenPrefix}</span>
			{formatCalendarDate(calendarDate)}
		</time>
	);
};

// `rows` are already sorted for display (buildAddonsPanel). The section only exists once the pane has loaded and
// its panel is ready or failed, so a request to scroll to it is handled the moment it mounts. The request is
// one-shot: after scrolling and focusing, the section calls `onHandled` and the page clears it, so a request
// can never fire again later (clicking the icon of the machine that is already selected sends a new request
// object, which scrolls again). Scrolling alone leaves keyboard and screen-reader focus on the icon, so the
// heading takes focus afterwards. The list is laid out in full (no inner scroll), so it is not a tab stop.
export const AddonsSection = ({ status, rows, request, onHandled }) => {
	const sectionRef = useRef(null);
	const headingRef = useRef(null);

	useEffect(() => {
		if (!request) {
			return;
		}

		sectionRef.current.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
		headingRef.current.focus({ preventScroll: true });
		onHandled();
	}, [request, onHandled]);

	const renderBody = () => {
		if (status === ADDONS_PANEL_STATUS.FAILED) {
			return <p className="mb-4">Licenses could not be loaded.</p>;
		}

		if (!rows.length) {
			return <p className="mb-4">No licenses.</p>;
		}

		return (
			<ul className="list-unstyled mb-4" role="list" aria-labelledby={HEADING_ID}>
				{rows.map((row) => {
					return (
						<li key={row.id} className="d-flex justify-content-between">
							<p className="me-2">
								<span>{row.name}</span>
								{row.tier && (
									<span className="text-muted text-nowrap ms-2">
										<span className="visually-hidden">Tier </span>
										{row.tier}
									</span>
								)}
							</p>
							<div className="d-flex align-items-baseline flex-shrink-0">
								{row.isTemporary && <Badge appearance="secondary" className="me-1">Temporary</Badge>}
								{renderStatusBadge(row.status)}
								{renderDate(row)}
							</div>
						</li>
					);
				})}
			</ul>
		);
	};

	return (
		<div ref={sectionRef}>
			<h6 id={HEADING_ID} ref={headingRef} tabIndex={-1} className="text-muted fs-6 fw-normal">Licenses</h6>
			{renderBody()}
		</div>
	);
};

AddonsSection.propTypes = {
	status: PropTypes.oneOf([ADDONS_PANEL_STATUS.READY, ADDONS_PANEL_STATUS.FAILED]).isRequired,
	rows: PropTypes.arrayOf(PropTypes.shape({
		id: PropTypes.string.isRequired,
		name: PropTypes.string.isRequired,
		tier: PropTypes.string,
		status: PropTypes.oneOf(Object.values(ADDON_ROW_STATUS)).isRequired,
		calendarDate: PropTypes.string,
		isForever: PropTypes.bool,
		isTemporary: PropTypes.bool,
		isTrial: PropTypes.bool,
	})).isRequired,
	request: PropTypes.shape({ machineId: PropTypes.string }),
	onHandled: PropTypes.func,
};

// One stable function: a new default per render would re-run the effect above.
AddonsSection.defaultProps = {
	request: null,
	onHandled: () => {},
};
